import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Check, ChevronDown, ChevronRight } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { EntitySearchSelect } from '@/components/ui/entity-search-select';
import { CrossEntityCondition as CrossCondition, MockEntity } from './flexible-filter-types';
import { fetchEntityFields } from '@/lib/api';
import { getOperatorsForFieldType } from '../shared/operators-config';

// Note: getOperatorsForFieldType is now imported from shared operators-config

interface CrossEntityConditionProps {
  condition: CrossCondition;
  entities: MockEntity[];
  onChange: (condition: CrossCondition) => void;
  // Context entities - when provided, entity selection will be hidden and auto-populated
  contextParentEntity?: string;
  contextChildEntity?: string;
}


// Field Search Select Component - Similar to Entity Search Select but for fields
const FieldSearchSelect: React.FC<{
  value: string;
  onValueChange: (value: string) => void;
  fields: any[];
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
}> = ({ value, onValueChange, fields, isLoading = false, disabled = false, placeholder = "Select field..." }) => {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const selectedField = fields.find(field => (field.logicalName || field.LogicalName) === value);

  // Performance optimization with memoization and capped results
  const filteredFields = React.useMemo(() => {
    if (!fields.length) return [];
    if (!searchQuery.trim()) return fields.slice(0, 50); // Limit initial display
    
    const query = searchQuery.toLowerCase();
    const results = fields.filter(field => 
      (field.displayName || '').toLowerCase().includes(query) ||
      (field.SchemaName || '').toLowerCase().includes(query) ||
      (field.LogicalName || '').toLowerCase().includes(query) ||
      (field.logicalName || '').toLowerCase().includes(query) ||
      (field.description || '').toLowerCase().includes(query)
    );
    
    return results.slice(0, 100); // Cap search results for performance
  }, [fields, searchQuery]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || isLoading}
          className="w-full justify-between bg-background px-3 font-normal outline-offset-0 hover:bg-background focus-visible:border-ring focus-visible:outline-[3px] focus-visible:outline-ring/20"
        >
          <span className={cn("truncate", !value && "text-muted-foreground")}> 
            {isLoading ? "Loading fields..." : selectedField 
              ? (selectedField.displayName || selectedField.SchemaName || selectedField.LogicalName || selectedField.logicalName)
              : placeholder
            }
          </span>
          <ChevronDown
            size={16}
            strokeWidth={2}
            className="shrink-0 text-muted-foreground/80"
            aria-hidden="true"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-full min-w-[var(--radix-popper-anchor-width)] border-input p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder="Type to search fields..." 
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            {filteredFields.length === 0 ? (
              <CommandEmpty>{isLoading ? "Loading..." : "No fields found."}</CommandEmpty>
            ) : (
              <CommandGroup>
                {filteredFields.map((field) => {
                  const fieldValue = field.logicalName || field.LogicalName;
                  const fieldDisplayName = field.displayName || field.SchemaName || field.LogicalName || field.logicalName;
                  return (
                    <CommandItem
                      key={fieldValue}
                      value={fieldValue}
                      onSelect={(currentValue) => {
                        onValueChange(currentValue === value ? "" : currentValue);
                        setOpen(false);
                        setSearchQuery("");
                      }}
                      className="cursor-pointer hover:bg-accent hover:text-accent-foreground"
                    >
                      <div className="flex flex-col w-full">
                        <span className="font-medium text-foreground">{fieldDisplayName}</span>
                        {fieldDisplayName !== fieldValue && (
                          <span className="text-xs text-muted-foreground/80">{fieldValue}</span>
                        )}
                      </div>
                      {value === fieldValue && (
                        <Check size={16} strokeWidth={2} className="ml-auto text-primary" />
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const CrossEntityCondition: React.FC<CrossEntityConditionProps> = ({
  condition,
  entities,
  onChange,
  contextParentEntity,
  contextChildEntity
}) => {


  // State for all entity fields (simplified)
  const [parentFields, setParentFields] = React.useState<any[]>([]);
  const [childFields, setChildFields] = React.useState<any[]>([]);
  const [loadingParentFields, setLoadingParentFields] = React.useState(false);
  const [loadingChildFields, setLoadingChildFields] = React.useState(false);

  // State for collapsible sections - start collapsed to save space
  const [childConditionsExpanded, setChildConditionsExpanded] = React.useState(false);
  const [parentConditionsExpanded, setParentConditionsExpanded] = React.useState(false);

  // Cache for fetched data to prevent duplicate calls
  const fetchCache = React.useRef<Record<string, any[]>>({});
  const prevEntityRef = React.useRef({ parent: '', child: '' });

  // Track previous condition to detect actual changes
  const prevConditionRef = React.useRef<string>(JSON.stringify(condition));

  
  // Helper function to update condition - NOT using useCallback to avoid dependency issues
  const updateCondition = (updates: Partial<CrossCondition>) => {
    const updated = { ...condition, ...updates };
    const updatedStr = JSON.stringify(updated);

    // Only call onChange if the condition actually changed
    if (updatedStr !== prevConditionRef.current) {
      prevConditionRef.current = updatedStr;
      onChange(updated);
    }
  };

  // Auto-populate entities from context when provided - run only once on mount
  const hasAutoPopulated = React.useRef(false);
  const autoPopulateInitialized = React.useRef({ parent: '', child: '' });

  React.useEffect(() => {
    // Check if we've already processed these exact context values
    if (hasAutoPopulated.current &&
        autoPopulateInitialized.current.parent === contextParentEntity &&
        autoPopulateInitialized.current.child === contextChildEntity) {
      return;
    }

    let needsUpdate = false;
    const updates: Partial<CrossCondition> = {};

    // Only populate if context is provided AND condition doesn't have a value yet
    if (contextParentEntity && !condition.parentEntity) {
      updates.parentEntity = contextParentEntity;
      needsUpdate = true;
    }

    if (contextChildEntity && !condition.childEntity) {
      updates.childEntity = contextChildEntity;
      needsUpdate = true;
    }

    if (needsUpdate) {
      hasAutoPopulated.current = true;
      autoPopulateInitialized.current = {
        parent: contextParentEntity || '',
        child: contextChildEntity || ''
      };

      // Use updateCondition to benefit from deduplication
      updateCondition(updates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextParentEntity, contextChildEntity]);

  // Fetch ALL parent fields using unified endpoint
  React.useEffect(() => {
    if (!condition.parentEntity) {
      setParentFields([]);
      return;
    }

    const fetchParentFields = async () => {
      // Skip if already cached
      const cacheKey = `parent_${condition.parentEntity}`;

      if (fetchCache.current[cacheKey]) {
        setParentFields(fetchCache.current[cacheKey]);
        return;
      }

      setLoadingParentFields(true);

      try {
        // Use unified field fetching - single API call gets all fields
        const fieldData = await fetchEntityFields(condition.parentEntity);

        const sortedFields = Array.isArray(fieldData) ? fieldData
          .sort((a, b) => (a.displayName || a.SchemaName || a.LogicalName || '').localeCompare(b.displayName || b.SchemaName || b.LogicalName || '')) : [];

        // Cache the results
        fetchCache.current[cacheKey] = sortedFields;
        setParentFields(sortedFields);

      } catch (error) {
        console.error('[CrossEntityCondition] Failed to fetch parent entity fields:', error);
        setParentFields([]);
      } finally {
        setLoadingParentFields(false);
      }
    };

    fetchParentFields();
  }, [condition.parentEntity]);

  // Don't automatically reset parent fields when entity changes to prevent clearing loaded filterset data
  // Users can manually clear fields if needed when changing entities
  React.useEffect(() => {
    prevEntityRef.current.parent = condition.parentEntity;
  }, [condition.parentEntity]);

  // Fetch ALL child fields using unified endpoint
  React.useEffect(() => {
    if (!condition.childEntity) {
      setChildFields([]);
      return;
    }

    const fetchChildFields = async () => {
      // Skip if already cached
      const cacheKey = `child_${condition.childEntity}`;

      if (fetchCache.current[cacheKey]) {
        setChildFields(fetchCache.current[cacheKey]);
        return;
      }

      setLoadingChildFields(true);

      try {
        // Use unified field fetching - single API call gets all fields
        const fieldData = await fetchEntityFields(condition.childEntity);

        const fields = Array.isArray(fieldData) ? fieldData : [];

        // Cache the results
        fetchCache.current[cacheKey] = fields;
        setChildFields(fields);

      } catch (error) {
        console.error('[CrossEntityCondition] Failed to fetch child entity fields:', error);
        setChildFields([]);
      } finally {
        setLoadingChildFields(false);
      }
    };

    fetchChildFields();
  }, [condition.childEntity]);

  // Don't automatically reset fields when entity changes to prevent clearing loaded filterset data
  // Users can manually clear fields if needed when changing entities
  React.useEffect(() => {
    prevEntityRef.current.child = condition.childEntity;
  }, [condition.childEntity]);

  const addChildCondition = () => {
    const newCondition = {
      id: `child_${Date.now()}`,
      field: '',
      operator: '',
      value: ''
    };
    updateCondition({
      childConditions: [...(condition.childConditions || []), newCondition]
    });
  };

  const updateChildCondition = (id: string, updates: any) => {
    const updatedConditions = (condition.childConditions || []).map(c =>
      c.id === id ? { ...c, ...updates } : c
    );
    updateCondition({ childConditions: updatedConditions });
  };

  const removeChildCondition = (id: string) => {
    const updatedConditions = (condition.childConditions || []).filter(c => c.id !== id);
    updateCondition({ childConditions: updatedConditions });
  };

  const addParentCondition = () => {
    const newCondition = {
      id: `parent_${Date.now()}`,
      field: '',
      operator: '',
      value: ''
    };
    updateCondition({
      parentConditions: [...(condition.parentConditions || []), newCondition]
    });
  };

  const updateParentCondition = (id: string, updates: any) => {
    const updatedConditions = (condition.parentConditions || []).map(c =>
      c.id === id ? { ...c, ...updates } : c
    );
    updateCondition({ parentConditions: updatedConditions });
  };

  const removeParentCondition = (id: string) => {
    const updatedConditions = (condition.parentConditions || []).filter(c => c.id !== id);
    updateCondition({ parentConditions: updatedConditions });
  };

  return (
    <div className="space-y-6">
      {/* Relationship Setup */}
      <div>
        <h4 className="font-semibold mb-2">
          {contextParentEntity && contextChildEntity ? 'Lookup Fields' : 'Entity Relationship'}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Show parent entity selection only if no context is provided */}
          {!contextParentEntity && (
            <div>
              <Label>Parent Entity</Label>
              <EntitySearchSelect
                value={condition.parentEntity}
                onValueChange={(value) => updateCondition({ parentEntity: value, parentLookupField: '' })}
                entities={entities}
                placeholder="Search for parent entity..."
              />
            </div>
          )}

          <div>
            <Label>
              {contextParentEntity ? `${contextParentEntity} Lookup Field` : 'Parent Lookup Field'}
            </Label>
            <FieldSearchSelect
              value={condition.parentLookupField}
              onValueChange={(value) => updateCondition({ parentLookupField: value })}
              fields={parentFields}
              isLoading={loadingParentFields}
              disabled={!condition.parentEntity || loadingParentFields}
              placeholder="Search all fields..."
            />
          </div>

          {/* Show child entity selection only if no context is provided */}
          {!contextChildEntity && (
            <div>
              <Label>Child Entity</Label>
              <EntitySearchSelect
                value={condition.childEntity}
                onValueChange={(value) => updateCondition({ childEntity: value, childLookupField: '' })}
                entities={entities}
                placeholder="Search for child entity..."
              />
            </div>
          )}

          <div>
            <Label>
              {contextChildEntity ? `${contextChildEntity} Lookup Field` : 'Child Lookup Field'}
            </Label>
            <FieldSearchSelect
              value={condition.childLookupField}
              onValueChange={(value) => updateCondition({ childLookupField: value })}
              fields={childFields}
              isLoading={loadingChildFields}
              disabled={!condition.childEntity || loadingChildFields}
              placeholder="Search all fields..."
            />
          </div>
        </div>

        {/* Visual Relationship Chain */}
        {condition.parentEntity && condition.childEntity && (
          <div className="mt-4 p-3 bg-muted rounded-lg">
            <div className="flex items-center gap-2 text-sm">
              <Badge variant={contextParentEntity ? "default" : "outline"}>
                {condition.parentEntity}
                {contextParentEntity && <span className="ml-1 text-xs">(from mapping)</span>}
              </Badge>
              <span>→</span>
              <Badge variant="outline">{condition.parentLookupField || 'select field'}</Badge>
              <span>→</span>
              <Badge variant={contextChildEntity ? "default" : "outline"}>
                {condition.childEntity}
                {contextChildEntity && <span className="ml-1 text-xs">(to mapping)</span>}
              </Badge>
              <span>→</span>
              <Badge variant="outline">{condition.childLookupField || 'select field'}</Badge>
            </div>
          </div>
        )}
      </div>

      {/* Child Conditions */}
      <Card>
        <CardHeader
          className="p-3 cursor-pointer"
          onClick={() => setChildConditionsExpanded(!childConditionsExpanded)}
        >
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              {childConditionsExpanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
              <CardTitle className="text-base">Child Conditions</CardTitle>
              <Badge variant="secondary" className="text-xs">
                {condition.childConditions?.length ?? 0}
              </Badge>
            </div>
            <Button 
              size="sm" 
              onClick={(e) => {
                e.stopPropagation();
                addChildCondition();
                setChildConditionsExpanded(true); // Auto-expand when adding
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Condition
            </Button>
          </div>
        </CardHeader>
        {childConditionsExpanded && (
          <CardContent className="p-3 pt-0 space-y-2">
          {(condition.childConditions || []).map(childCond => (
            <div key={childCond.id} className="grid grid-cols-1 md:grid-cols-4 gap-2 p-2 border rounded-lg">
              <div>
                <Label>Field</Label>
                <FieldSearchSelect
                  value={childCond.field}
                  onValueChange={(value) => updateChildCondition(childCond.id, { field: value })}
                  fields={childFields}
                  isLoading={loadingChildFields}
                  disabled={!condition.childEntity || loadingChildFields}
                  placeholder="Search fields..."
                />
              </div>

              <div>
                <Label>Operator</Label>
                <Select
                  value={childCond.operator}
                  onValueChange={(value) => {
                    const field = childFields.find(f => 
                      (f.logicalName || f.LogicalName) === childCond.field
                    );
                    const operators = getOperatorsForFieldType(field?.AttributeType || field?.type);
                    const selectedOp = operators.find(op => op.value === value);
                    
                    // If the operator doesn't require a value, clear the value
                    const updates: any = { operator: value };
                    if (selectedOp && !selectedOp.requiresValue) {
                      updates.value = '';
                    }
                    
                    updateChildCondition(childCond.id, updates);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={!childCond.field ? "Select field first" : "Select operator"} />
                  </SelectTrigger>
                  <SelectContent>
                    {(childCond.field ? getOperatorsForFieldType(
                      childFields.find(f => 
                        (f.logicalName || f.LogicalName) === childCond.field
                      )?.AttributeType || 
                      childFields.find(f => 
                        (f.logicalName || f.LogicalName) === childCond.field
                      )?.type
                    ) : getOperatorsForFieldType()).map(op => (
                      <SelectItem key={op.value} value={op.value}>
                        {op.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Value</Label>
                {
                  (!childCond.field || !childCond.operator) ? (
                    <Input
                      value=""
                      placeholder="Select field and operator first"
                      disabled
                    />
                  ) : (
                    ((() => {
                      const field = childFields.find(f => (f.logicalName || f.LogicalName) === childCond.field);
                      const validOperators = getOperatorsForFieldType(field?.AttributeType || field?.type);
                      const selectedOperator = validOperators.find(op => op.value === childCond.operator);
                      const requiresValue = selectedOperator?.requiresValue;
                      
                      return requiresValue ? (
                        <Input
                          value={childCond.value?.toString() || ''}
                          onChange={(e) => updateChildCondition(childCond.id, { value: e.target.value })}
                          placeholder="Enter value"
                        />
                      ) : null;
                    })())
                  )
                }
              </div>

              <div className="flex items-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeChildCondition(childCond.id)}
                  className="text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}

            {(condition.childConditions?.length ?? 0) === 0 && (
              <div className="text-center py-4 text-muted-foreground">
                No child conditions defined. Click "Add Condition" to get started.
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Parent Conditions */}
      <Card>
        <CardHeader
          className="p-3 cursor-pointer"
          onClick={() => setParentConditionsExpanded(!parentConditionsExpanded)}
        >
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              {parentConditionsExpanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
              <CardTitle className="text-base">Parent Conditions (Optional)</CardTitle>
              <Badge variant="secondary" className="text-xs">
                {condition.parentConditions?.length ?? 0}
              </Badge>
            </div>
            <Button 
              size="sm" 
              onClick={(e) => {
                e.stopPropagation();
                addParentCondition();
                setParentConditionsExpanded(true); // Auto-expand when adding
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Condition
            </Button>
          </div>
        </CardHeader>
        {parentConditionsExpanded && (
          <CardContent className="p-3 pt-0 space-y-2">
          {(condition.parentConditions || []).map(parentCond => (
            <div key={parentCond.id} className="grid grid-cols-1 md:grid-cols-4 gap-2 p-2 border rounded-lg">
              <div>
                <Label>Field</Label>
                <FieldSearchSelect
                  value={parentCond.field}
                  onValueChange={(value) => updateParentCondition(parentCond.id, { field: value })}
                  fields={parentFields}
                  isLoading={loadingParentFields}
                  disabled={!condition.parentEntity || loadingParentFields}
                  placeholder="Search fields..."
                />
              </div>

              <div>
                <Label>Operator</Label>
                <Select
                  value={parentCond.operator}
                  onValueChange={(value) => {
                    const field = parentFields.find(f => 
                      (f.logicalName || f.LogicalName) === parentCond.field
                    );
                    const operators = getOperatorsForFieldType(field?.AttributeType || field?.type);
                    const selectedOp = operators.find(op => op.value === value);
                    
                    // If the operator doesn't require a value, clear the value
                    const updates: any = { operator: value };
                    if (selectedOp && !selectedOp.requiresValue) {
                      updates.value = '';
                    }
                    
                    updateParentCondition(parentCond.id, updates);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={!parentCond.field ? "Select field first" : "Select operator"} />
                  </SelectTrigger>
                  <SelectContent>
                    {(parentCond.field ? getOperatorsForFieldType(
                      parentFields.find(f => 
                        (f.logicalName || f.LogicalName) === parentCond.field
                      )?.AttributeType || 
                      parentFields.find(f => 
                        (f.logicalName || f.LogicalName) === parentCond.field
                      )?.type
                    ) : getOperatorsForFieldType()).map(op => (
                      <SelectItem key={op.value} value={op.value}>
                        {op.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Value</Label>
                {
                  (!parentCond.field || !parentCond.operator) ? (
                    <Input
                      value=""
                      placeholder="Select field and operator first"
                      disabled
                    />
                  ) : (
                    ((() => {
                      const field = parentFields.find(f => (f.logicalName || f.LogicalName) === parentCond.field);
                      const validOperators = getOperatorsForFieldType(field?.AttributeType || field?.type);
                      const selectedOperator = validOperators.find(op => op.value === parentCond.operator);
                      const requiresValue = selectedOperator?.requiresValue;
                      
                      return requiresValue ? (
                        <Input
                          value={parentCond.value?.toString() || ''}
                          onChange={(e) => updateParentCondition(parentCond.id, { value: e.target.value })}
                          placeholder="Enter value"
                        />
                      ) : null;
                    })())
                  )
                }
              </div>

              <div className="flex items-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeParentCondition(parentCond.id)}
                  className="text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}

            {(condition.parentConditions?.length ?? 0) === 0 && (
              <div className="text-center py-4 text-muted-foreground">
                No parent conditions defined. Click "Add Condition" to add filters on the parent entity.
              </div>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
};
